import { isEmpty } from 'lodash';

import {
  ENTIRE_AVAILABLE,
  FILTER_BODYTYPE,
  FILTER_DIRECT_SERVICES_ONLY,
  FILTER_HAULING_CARGO_AIRCRAFT_ONLY,
  FILTER_HIDE_RFS,
  MODE_AIR,
  MODE_WATER,
} from '@/lib/constants';
import { AircraftAttributes, FilterSelection, RouteBlockInterface, RouteScheduleInterface } from '@/types';

export const filterStandaloneScheduleOptions = <T extends RouteBlockInterface | RouteScheduleInterface>(
  options: T[],
  filters: FilterSelection,
): T[] => {
  if (isEmpty(filters)) return options;

  // Loops through Schedules and removes any that don't match ALL set conditions
  return options.filter((option) => {
    const matches = [];

    if (!isEmpty(filters.carrierIds)) {
      if (option.mode === MODE_AIR || option.mode === MODE_WATER) {
        const { primaryCarriers: carriers } = option.attributes || {};

        matches.push(carriers?.some((carrier) => filters.carrierIds?.includes(carrier)));
      }
    }

    if (filters[FILTER_DIRECT_SERVICES_ONLY]) {
      if (option.mode === MODE_AIR && option.attributes?.transferCount) {
        // Ensure stops are included in Air "direct"
        matches.push(option.attributes.transferCount === 0 && option.attributes.stopCount === 0);
      }
      if (option.mode === MODE_WATER && option.attributes?.transferCount) {
        matches.push(option.attributes.transferCount === 0);
      }
    }

    // AIR ONLY filters
    if (option.mode === MODE_AIR) {
      // Filter by body type (NarrowBody, Widebody)
      if (filters[FILTER_BODYTYPE] && !isEmpty(filters[FILTER_BODYTYPE])) {
        if (filters[FILTER_BODYTYPE].length === 2) {
          matches.push(true); // If both body types are selected, return all options
        } else {
          filters[FILTER_BODYTYPE].forEach((filter) => {
            if (option.attributes?.aircraft) {
              matches.push(option.attributes.aircraft[filter as keyof AircraftAttributes] === ENTIRE_AVAILABLE);
            }
          });
        }
      }

      // Hide RFS (Road feeder Services) if any vehicle is RFS
      if (filters[FILTER_HIDE_RFS]) {
        matches.push(!option.attributes.hasRfs);
      }

      // Freight vehicles only (for the entire route)
      if (filters[FILTER_HAULING_CARGO_AIRCRAFT_ONLY]) {
        matches.push(option.attributes?.hasFreightOnlyVehicles === ENTIRE_AVAILABLE);
      }
    }

    // If any matches were checked, return false if any didn't pass the check
    // or just return true (assumes no filters apply)
    return matches.length ? !matches.includes(false) : true;
  });
};
